// DD Consulting — Partners & Strategic Alliances
// Single source of truth. Any partner rendering surface (Hero TrustBar on /,
// About > DD Ecosystem, etc.) reads from this list.
//
// Schema:
//   id            Stable kebab-case identifier
//   name          Display name
//   url           External homepage URL
//   logo          Absolute public path under /partners
//   logoWhite     (optional) Alt logo for dark tiles
//   alt           Accessible alt text / aria-label
//   tier          'featured' — top-tier technology / platform partner (shown everywhere)
//                 'technology' — shown in About > Technology Stack column
//                 'alliance' — shown in About > Strategic Alliances column
//                 A partner can have multiple tiers (array).
//   capabilities  Short capability chips
//   description   One-line descriptor for hover / aria
//   internalLink  (optional) If present, clicking the logo routes here
//                 instead of opening the external site
//
// Rendering components:
//   - components/sections/Hero.jsx   → tier includes 'featured'
//   - components/pages/About.jsx     → tier includes 'technology' for Col A,
//                                      tier includes 'alliance'   for Col B

export const PARTNERS = [
  // ——————————————————————————— Featured / Technology tier ———————————————————————————
  {
    id: "aws",
    name: "AWS",
    fullName: "Amazon Web Services",
    url: "https://aws.amazon.com/",
    internalLink: "/services/aws",
    tier: ["technology"],          // Removed "featured" — badge entry below handles TrustBar
    capabilities: ["Cloud Infrastructure", "Bedrock AI", "Sovereign Cloud"],
    description: "AWS Advanced Tier Partner. DD Consulting architects secure-by-design workloads in the AWS Sydney Region.",
    alt: "TECHNOLOGY: AMAZON_WEB_SERVICES // CLOUD_PARTNER",
    // Renders as inline brand <svg> in components — no raster asset needed.
    renderMode: "inline-svg",
    svgId: "aws",
  },
  {
    id: "aws-advanced-tier",
    name: "AWS Advanced Tier",
    fullName: "Amazon Web Services – Advanced Tier Services Partner",
    url: "https://aws.amazon.com/",
    internalLink: "/services/aws",
    tier: ["featured"],            // Shows in Hero TrustBar only
    capabilities: ["Cloud Infrastructure", "Bedrock AI", "Sovereign Cloud", "Advanced Tier"],
    description: "AWS Advanced Tier Services Partner. DD Consulting delivers governed, secure-by-design AWS workloads from the Sydney Region.",
    alt: "CERTIFICATION: AWS_PARTNER // ADVANCED_TIER_SERVICES",
    renderMode: "badge",
    logo: "/partners/aws-advanced-tier.png",
  },
  {
    id: "microsoft",
    name: "Microsoft Azure",
    fullName: "Microsoft Azure",
    url: "https://azure.microsoft.com/",
    tier: ["technology"],
    capabilities: ["Cloud Infrastructure", "Azure OpenAI", "M365"],
    description: "Microsoft Azure technology partner for enterprise-grade AI and identity.",
    alt: "TECHNOLOGY: MICROSOFT_AZURE // CLOUD_PARTNER",
    renderMode: "inline-svg",
    svgId: "microsoft",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    fullName: "HubSpot",
    url: "https://www.hubspot.com/",
    // HubSpot appears in the Hero TrustBar as a featured partner, in the
    // About Technology Stack column as a platform, AND in the Strategic
    // Alliances column as a commercial relationship.
    tier: ["featured", "technology", "alliance"],
    capabilities: ["CRM", "Marketing Automation", "Sales Enablement", "Inbound Marketing", "RevOps"],
    description: "HubSpot — CRM, marketing automation and RevOps platform. DD Consulting implements governed HubSpot stacks for Australian SMBs and enterprises.",
    alt: "PLATFORM: HUBSPOT // CRM_REVOPS_PARTNER",
    renderMode: "image",
    logo: "/partners/hubspot.svg",
    logoWhite: "/partners/hubspot-dark.svg",
  },

  // ——————————————————————————— Strategic Alliances ———————————————————————————
  {
    id: "neon-snow",
    name: "Neon Snow",
    fullName: "Neon Snow",
    url: null,
    tier: ["alliance"],
    capabilities: ["Creative Delivery"],
    description: "Neon Snow — strategic delivery alliance.",
    alt: "ALLIANCE: NEON_SNOW // STRATEGIC_PARTNER",
    renderMode: "image",
    logo: "https://customer-assets.emergentagent.com/job_web-updates-4/artifacts/bt5t159q_neon.png",
  },
  {
    id: "darevolution",
    name: "Darevolution",
    fullName: "Darevolution",
    url: null,
    tier: ["alliance"],
    capabilities: ["Strategy & Transformation"],
    description: "Darevolution — strategic transformation alliance.",
    alt: "ALLIANCE: DAREVOLUTION // STRATEGIC_PARTNER",
    renderMode: "image",
    logo: "https://customer-assets.emergentagent.com/job_web-updates-4/artifacts/fhn4gwzo_Darevolution-logo%20%281%29.png",
  },
  {
    id: "assureqa",
    name: "AssureQA",
    fullName: "AssureQA",
    url: null,
    tier: ["alliance"],
    capabilities: ["Quality Assurance", "Test Automation"],
    description: "AssureQA — assurance and test automation alliance.",
    alt: "ALLIANCE: ASSUREQA // STRATEGIC_PARTNER",
    renderMode: "image",
    logo: "https://customer-assets.emergentagent.com/job_web-updates-4/artifacts/hm3tnr65_assureqa.avif",
  },
  {
    id: "blue-pearl",
    name: "Blue Pearl",
    fullName: "Blue Pearl",
    url: null,
    tier: ["alliance"],
    capabilities: ["Advisory"],
    description: "Blue Pearl — strategic advisory alliance.",
    alt: "ALLIANCE: BLUE_PEARL // STRATEGIC_PARTNER",
    renderMode: "image",
    logo: "https://customer-assets.emergentagent.com/job_web-updates-4/artifacts/x8h7cozt_BP.jpeg",
  },
  {
    id: "fx1",
    name: "FX1",
    fullName: "FX1",
    url: "https://fx1.io/",
    tier: ["featured", "alliance"],
    capabilities: ["Creative Production", "Brand", "UX", "Content"],
    description: "FX1 — creative and digital production studio. Alliance partner for bespoke brand, UX and content execution alongside DD Consulting's strategy and engineering.",
    alt: "ALLIANCE: FX1 // CREATIVE_PRODUCTION_PARTNER",
    renderMode: "image",
    logo: "/partners/fx1.png",
  },
  {
    id: "three-digital",
    name: "Three Digital",
    fullName: "Three Digital",
    url: "https://threedigital.com.au/",
    tier: ["featured", "alliance"],
    capabilities: ["Performance Marketing", "Digital Growth", "Paid Media"],
    description: "Three Digital — Australian digital agency. Alliance partner for performance marketing and digital growth programs complementing DD Consulting's AI and governance work.",
    alt: "ALLIANCE: THREE_DIGITAL // DIGITAL_GROWTH_PARTNER",
    renderMode: "image",
    logo: "/partners/three-digital.svg",
    logoWhite: "/partners/three-digital-white.svg",
  },
];

// Convenience selectors
export const getFeatured = () => PARTNERS.filter(p => p.tier.includes("featured"));
export const getTechnology = () => PARTNERS.filter(p => p.tier.includes("technology"));
export const getAlliances = () => PARTNERS.filter(p => p.tier.includes("alliance"));
